import styled from '@emotion/styled';
import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  buttonIds,
  getRecaptchaToken,
  gtmEvents,
  responseTypes, routesPaths, sendFunnelAnalytics,
  socialType
} from '@selfserve/react-utils';
import {
  storage,
  storageKeys,
  useLog,
  useQueryParams,
  useStorage,
  GoogleButtonText,
  GoogleOAuthResult,
  useGoogleOAuth,
} from '@selfserve/react-hooks';
import { IWrappedComponentProps } from '@selfserve/react-hocs';
import { GoogleButton } from '@selfserve/react-ui';
import { useSocialRegistrationRequest } from '../data';

const NX_FACEBOOK_APP_ID = process.env['NX_FACEBOOK_APP_ID'];
const ALREADY_REGISTERED_ERROR_ID = 10019;

const StyledContainer = styled('div')(({ theme }: { theme?: any }) => ({
  marginBottom: theme.spacing(3),
}));

const StyledButtons = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  gridGap: theme.spacing(3),
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4),
}));

const StyledFacebookButton = styled('div')(({ theme, disabled }: { theme?: any, disabled?: boolean }) => ({
  border: `1px solid ${theme.palette.grey['400']}`,
  borderRadius: '4px',
  height: '40px',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...(!disabled && {
    '&:hover': {
      backgroundColor: theme.palette.grey['600'],
    },
  }),
  ...(disabled && {
    pointerEvents: 'none',
    cursor: 'default',
  }),
}));

const StyledImage = styled('div')(
  ({ theme, src }: { theme?: any; src?: string }) => ({
    borderRight: `1px solid ${theme.palette.grey['400']}`,
    width: '40px',
    height: '40px',
    background: `url("${src}") no-repeat center`,
    pointerEvents: 'none',
  })
);

const StyledName = styled('div')(({ theme }: { theme?: any }) => ({
  width: '100%',
  textAlign: 'center',
  color: theme.palette.grey['100'],
  fontFamily: 'Centra No1',
  fontWeight: '500',
  pointerEvents: 'none',
  lineHeight: '21px',
  marginRight: '4px',
  marginLeft: '4px',
}));

const StyledDivider = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  alignItems: 'center',
}));

const StyledDividerLine = styled('div')(({ theme }: { theme?: any }) => ({
  flex: 1,
  background: theme.palette.grey['400'],
  height: '1px',
}));

const StyledDividerText = styled('div')(({ theme }: { theme?: any }) => ({
  textTransform: 'uppercase',
  fontSize: '16px',
  fontFamily: 'Centra No1',
  fontWeight: '500',
  padding: '0 7px',
  color: theme.palette.grey['300'],
}));

type Props = IWrappedComponentProps & {
  setFieldError: (field: string, message: string) => void;
  onLoadingChange: (isLoading: boolean) => void;
  loading: boolean;
  options: string[];
};

export const SocialRegistration = ({ openErrorModal, setFieldError, onLoadingChange, loading, options }: Props) => {
  const { t } = useTranslation();
  const { log } = useLog();
  const { desiredPackageId } = useParams();
  const queryParams = useQueryParams();
  const [currentExistingUser, setExistingUser] = useStorage(
    storage.LOCAL,
    storageKeys.EXISTING_USER,
    false
  );
  const { mutateAsync: handleSocialRegistrationRequest, variables, isLoading } =
    useSocialRegistrationRequest();

  const responseHandle = useCallback(
    (email: string, status?: number, url?: string, message?: string, existingUser?: boolean, id?: number) => {
      if (String(status).startsWith(responseTypes.SUCCESS)) {
        if (existingUser) {
          setExistingUser(existingUser);
        } else if (currentExistingUser) {
          setExistingUser(false);
        }
        if ((window as any).dataLayer) {
          (window as any).dataLayer.push({
            event: gtmEvents.EMAIL_SUBMITION,
            email,
          });
        }
        window.location.replace(url as string);
      } else if (message) {
        setFieldError('email', message);
        if (id === ALREADY_REGISTERED_ERROR_ID) {
          openErrorModal({
            url: routesPaths.LOGIN,
            text: t(`errors.${ALREADY_REGISTERED_ERROR_ID}`),
            confirmBtnText: t('common.login'),
            preventActionsOnClose: true,
            withDissmiss: true,
          });
        }
      } else {
        openErrorModal({ text: t(`oauth.${variables?.type}-error`) });
      }
    },
    [setFieldError, openErrorModal, variables, t]
  );

  const handleFacebookLogin = useCallback(() => {
    (window as any).FB.login(
      (response: any) => {
        if (response.authResponse) {
          (window as any).FB.api(
            '/me?fields=email',
            ({ email }: { email?: string } = {}) => {
              const { accessToken: token, userID: userId } =
                response.authResponse;
              getRecaptchaToken()
                .then((recaptchaToken: string) =>
                  handleSocialRegistrationRequest({
                    ...queryParams,
                    type: socialType.FACEBOOK,
                    data: { token, userId },
                    desiredPackageId,
                    recaptchaToken,
                  })
                )
                .then(({ status, data }) =>
                  responseHandle(
                    email as string,
                    status,
                    data?.url,
                    data?.message_en,
                    data?.existingUser,
                    data?.id
                  )
                )
                .catch((error: Error) => {
                  log(error);
                  openErrorModal({
                    text: t(`oauth.${socialType.FACEBOOK}-error`),
                  });
                });
            }
          );
        }
      },
      { scope: 'email' }
    );
  }, [desiredPackageId, queryParams, handleSocialRegistrationRequest, openErrorModal]);

  const handleGoogleSuccess = useCallback(
    (result: GoogleOAuthResult) => {
      getRecaptchaToken()
        .then((recaptchaToken: string) =>
          handleSocialRegistrationRequest({
            ...queryParams,
            type: socialType.GOOGLE,
            data: { token: result.token },
            desiredPackageId,
            recaptchaToken,
          })
        )
        .then(({ status, data }) =>
          responseHandle(
            result.email,
            status,
            data?.url,
            data?.message_en,
            data?.existingUser,
            data?.id
          )
        )
        .catch((error: Error) => {
          log(error);
          sessionStorage.removeItem(storageKeys.SOCIAL_TYPE);
          openErrorModal({ text: t(`oauth.${socialType.GOOGLE}-error`) });
        });
    },
    [handleSocialRegistrationRequest, queryParams, desiredPackageId, responseHandle, log, openErrorModal, t]
  );

  const handleGoogleError = useCallback(
    (error: Error) => {
      log(error);
      sessionStorage.removeItem(storageKeys.SOCIAL_TYPE);
      openErrorModal({ text: t(`oauth.${socialType.GOOGLE}-error`) });
    },
    [log, openErrorModal, t]
  );

  const { googleButtonRef } = useGoogleOAuth({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
    text: GoogleButtonText.CONTINUE_WITH,
    onClick: () => {
      sessionStorage.setItem(storageKeys.SOCIAL_TYPE, socialType.GOOGLE);
      sendFunnelAnalytics({
        type: 'buttonClicked',
        page: '',
        event: 'signupMethodSelected',
        properties: {
          method: socialType.GOOGLE,
        }
      }, desiredPackageId);
    },
  });

  useEffect(() => onLoadingChange(isLoading), [isLoading]);

  useEffect(() => {
    (window as any).FB?.init({
      appId: NX_FACEBOOK_APP_ID,
      cookie: true, // Enable cookies to allow the server to access the session.
      xfbml: true, // Parse oauth plugins on this webpage.
      version: 'v13.0', // Use this Graph API version for this call.
    });
  }, []);

  return (
    <StyledContainer className="oauth-block">
      <StyledButtons className="oauth-buttons">
        {options.includes(socialType.GOOGLE) && (
          <GoogleButton
            buttonRef={googleButtonRef}
            className="oauth-button"
            id={buttonIds.googleRegistration}
            disabled={loading}
          />
        )}
        {options.includes(socialType.FACEBOOK) && (
          <StyledFacebookButton
            onClick={handleFacebookLogin}
            className="oauth-button"
            id={buttonIds.facebookRegistration}
            disabled={loading}
          >
            <StyledImage
              className={`oauth-icon ${socialType.FACEBOOK}`}
              src={`assets/${socialType.FACEBOOK}.svg`}
            />
            <StyledName className="oauth-button-text">
              {t(`oauth.${socialType.FACEBOOK}`)}
            </StyledName>
          </StyledFacebookButton>
        )}
      </StyledButtons>
      <StyledDivider className="divider">
        <StyledDividerLine className="divider-line" />
        <StyledDividerText className="divider-or">
          {t('get-started.or-sign-up-with')}
        </StyledDividerText>
        <StyledDividerLine className="divider-line" />
      </StyledDivider>
    </StyledContainer>
  );
};
